import { useState, useRef } from 'react';
import {
  Box, TextField, Button, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
  Typography, Divider, Chip,
} from '@mui/material';
import { Search, ExpandMore, UploadFile } from '@mui/icons-material';

export interface SitemapFormValues {
  url: string;
  sitemapUrl?: string;
  xmlContent?: string;
}

interface Props {
  loading: boolean;
  defaultUrl?: string;
  onSubmit: (values: SitemapFormValues) => void;
}

const MAX_XML_BYTES = 1_048_576; // 1 MB

export default function SitemapAnalyzeForm({ loading, defaultUrl = '', onSubmit }: Props) {
  const [url, setUrl]             = useState(defaultUrl);
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [fileName, setFileName]   = useState('');
  const [xmlContent, setXmlContent] = useState('');
  const [urlError, setUrlError]   = useState('');
  const [advError, setAdvError]   = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_XML_BYTES) {
      setAdvError('File must be under 1 MB.');
      return;
    }
    setAdvError('');
    setSitemapUrl(''); // mutually exclusive
    const reader = new FileReader();
    reader.onload = (ev) => {
      setXmlContent((ev.target?.result as string) ?? '');
      setFileName(file.name);
    };
    reader.readAsText(file);
  };

  const handleSitemapUrlChange = (v: string) => {
    setSitemapUrl(v);
    if (v) {
      // clear uploaded file — mutually exclusive
      setXmlContent('');
      setFileName('');
      if (fileRef.current) fileRef.current.value = '';
    }
    if (advError) setAdvError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedUrl = url.trim();
    if (!trimmedUrl) { setUrlError('Please enter a website URL.'); return; }
    if (!/^https?:\/\/.+/.test(trimmedUrl)) { setUrlError('URL must start with http:// or https://'); return; }
    setUrlError('');

    const trimmedSitemapUrl = sitemapUrl.trim();
    if (trimmedSitemapUrl && !/^https?:\/\/.+/.test(trimmedSitemapUrl)) {
      setAdvError('Sitemap URL must start with http:// or https://');
      return;
    }
    setAdvError('');

    onSubmit({
      url: trimmedUrl,
      sitemapUrl: trimmedSitemapUrl || undefined,
      xmlContent: xmlContent || undefined,
    });
  };

  const hasAdvanced = !!(sitemapUrl || xmlContent);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* ── Primary input ── */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          fullWidth
          size="medium"
          label="Website URL"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => { setUrl(e.target.value); if (urlError) setUrlError(''); }}
          error={!!urlError}
          helperText={urlError}
          disabled={loading}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search color="action" /></InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 280 }}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ height: 56, px: 4, minWidth: 170 }}
        >
          {loading ? 'Analyzing…' : 'Discover Sitemap'}
        </Button>
      </Box>

      {/* ── Advanced options ── */}
      <Accordion disableGutters elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2" color="text.secondary">
            Advanced options
            {hasAdvanced && (
              <Chip label="override active" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {/* Sitemap URL override */}
          <TextField
            fullWidth
            size="small"
            label="Sitemap URL (optional)"
            placeholder="https://example.com/wp-sitemap.xml"
            value={sitemapUrl}
            onChange={(e) => handleSitemapUrlChange(e.target.value)}
            error={!!advError && !!sitemapUrl}
            helperText={!!sitemapUrl && advError ? advError : 'Skip auto-discovery and validate this URL directly.'}
            disabled={loading || !!xmlContent}
            sx={{ mb: 2 }}
          />

          {/* OR divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.disabled">OR</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          {/* XML upload */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <input
              ref={fileRef}
              type="file"
              accept=".xml,application/xml,text/xml"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={loading || !!sitemapUrl}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFile />}
              onClick={() => fileRef.current?.click()}
              disabled={loading || !!sitemapUrl}
            >
              Upload Sitemap XML
            </Button>
            {fileName ? (
              <Typography variant="body2" color="text.secondary">{fileName}</Typography>
            ) : (
              <Typography variant="caption" color="text.disabled">No file selected</Typography>
            )}
            {fileName && !sitemapUrl && (
              <Button size="small" color="inherit"
                onClick={() => { setXmlContent(''); setFileName(''); if (fileRef.current) fileRef.current.value = ''; }}>
                Clear
              </Button>
            )}
          </Box>
          {!!advError && !sitemapUrl && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{advError}</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
